import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Result from 'antd/lib/result';
import {
  ArrowClockwise,
  ListChecks,
  PhoneIncoming,
  PhoneOutgoing,
} from '@phosphor-icons/react';
import { withRouter } from 'react-router-dom';
import VoiceInbound from './VoiceInbound';
import './voice.scss';
import Button from '../../components/Button';
import RadioPicker from '../RadioPicker';
import VoiceOutbound from './VoiceOutbound';
import VoiceFollowUps from './VoiceFollowUps';
import { verifyFeatureAccess } from '../../global/featureFlags';

const pageFromSearch = (search) => {
  const params = new URLSearchParams(search);
  const type = params.get('type');
  return ['Inbound', 'Outbound', 'Follow Up'].includes(type) ? type : 'Inbound';
};

const Voice = ({
  location,
  history,
  accessVoiceInbox,
}) => {
  const [reset, setReset] = useState(false);
  const [canUseCallFollowups, setCanUseCallFollowups] = useState(null);
  const requestedPage = useMemo(() => pageFromSearch(location.search), [location.search]);
  const followUpAccessPending = requestedPage === 'Follow Up' && canUseCallFollowups === null;
  const page = requestedPage === 'Follow Up' && canUseCallFollowups === false ? 'Inbound' : requestedPage;
  const renderedPage = followUpAccessPending ? null : page;

  const pageOptions = [
    { label: 'Inbound', value: 'Inbound', icon: PhoneIncoming },
    { label: 'Outbound', value: 'Outbound', icon: PhoneOutgoing },
    ...(canUseCallFollowups ? [{ label: 'Follow Up', value: 'Follow Up', icon: ListChecks }] : []),
  ];

  const fallbackToInbound = () => {
    const params = new URLSearchParams();
    params.set('type', 'Inbound');
    history.replace(`${location.pathname}?${params.toString()}`);
  };

  useEffect(() => {
    let active = true;
    verifyFeatureAccess('call_followups')
      .then((hasAccess) => {
        if (!active) return;

        setCanUseCallFollowups(!!hasAccess);
      })
      .catch(() => {
        if (!active) return;

        setCanUseCallFollowups(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (requestedPage === 'Follow Up' && canUseCallFollowups === false) fallbackToInbound();
  }, [requestedPage, canUseCallFollowups, location.pathname]);

  const handlePageChange = (newPage) => {
    if (newPage === 'Follow Up' && canUseCallFollowups !== true) return;

    const params = new URLSearchParams();
    params.set('type', newPage);
    history.replace(`${location.pathname}?${params.toString()}`);
  };

  if (!accessVoiceInbox) {
    return (
      <Result
        status="403"
        title="Access Denied"
        subTitle="You do not have permission to access Voice."
        extra={(
          <Button
            type="primary"
            label="Go to Home"
            onClick={() => history.push('/app')}
          />
        )}
      />
    );
  }

  return (
    <div className="voice-wrapper">
      <div className="voice-header">
        <div className="voice-header-title">
          <RadioPicker
            options={pageOptions}
            value={page}
            setValue={handlePageChange}
          />
          <div className="voice-header-refresh">
            <Button
              label="Refresh"
              type="secondary"
              size="xsmall"
              leftIcon={<ArrowClockwise size={16} weight="fill" />}
              onClick={() => setReset(true)}
            />
          </div>
        </div>
      </div>
      {renderedPage === 'Inbound' && <VoiceInbound reset={reset} setReset={setReset} />}
      {renderedPage === 'Outbound' && <VoiceOutbound page={page} reset={reset} setReset={setReset} />}
      {renderedPage === 'Follow Up' && canUseCallFollowups === true && (
        <VoiceFollowUps page={page} reset={reset} setReset={setReset} />
      )}
    </div>
  );
};

Voice.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func,
    push: PropTypes.func,
  }).isRequired,
  accessVoiceInbox: PropTypes.bool,
};

Voice.defaultProps = {
  accessVoiceInbox: false,
};

export { Voice };

const mapStateToProps = (state) => ({
  accessVoiceInbox: state.auth.accessVoiceInbox,
});

export default withRouter(connect(mapStateToProps)(Voice));
